import type { ReactNode } from 'react';
import type { ContactMessage } from '../../app/legacy-types';

export type ContactCustomEmoji = {
  customEmojiId: string;
  offset?: number;
  length?: number;
  fallbackText?: string;
  mimeType?: string;
  renderMode?: 'IMAGE' | 'VIDEO' | 'STATIC_FALLBACK' | string;
  url?: string;
};

export type ContactMediaAsset = {
  id: string;
  provider?: string | null;
  asset_kind: 'EMOJI' | 'STICKER';
  title: string;
  emoji_text?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  is_animated?: boolean;
  renderMode?: 'IMAGE' | 'VIDEO' | 'STATIC_FALLBACK' | string | null;
  url?: string | null;
};

export function safeContactMediaUrl(item: Pick<ContactCustomEmoji, 'customEmojiId' | 'renderMode' | 'url'>): string {
  const identifier = String(item.customEmojiId || '').trim();
  if (!identifier) return '';
  const fallback = item.renderMode === 'STATIC_FALLBACK' ? '?fallback=1' : '';
  return `/api/v1/contact-center/media/custom-emoji/${encodeURIComponent(identifier)}${fallback}`;
}

export function safeLibraryMediaUrl(assetId: string): string {
  const identifier = String(assetId || '').trim();
  return identifier ? `/api/v1/contact-center/media/library/${encodeURIComponent(identifier)}` : '';
}

function CustomEmojiMedia({ item }: { item: ContactCustomEmoji }) {
  const source = safeContactMediaUrl(item);
  const fallback = item.fallbackText || '✨';
  if (!source) return <>{fallback}</>;
  if (item.renderMode === 'VIDEO' || String(item.mimeType || '').toLowerCase().startsWith('video/')) {
    return <video className="contact-custom-emoji" src={source} aria-label={fallback} autoPlay muted loop playsInline preload="metadata" />;
  }
  return <img className="contact-custom-emoji" src={source} alt={fallback} loading="lazy" referrerPolicy="no-referrer" />;
}

function customEmojiRows(message: ContactMessage): ContactCustomEmoji[] {
  const value = message.payload?._customEmoji;
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is ContactCustomEmoji => Boolean(item && typeof item === 'object' && String((item as ContactCustomEmoji).customEmojiId || '').trim()));
}

export function SafeMessageBody({ message }: { message: ContactMessage }) {
  const body = String(message.body || '');
  const descriptors = customEmojiRows(message)
    .map(item => ({ ...item, offset: Number(item.offset), length: Number(item.length) }))
    .filter(item => Number.isInteger(item.offset) && item.offset >= 0 && Number.isInteger(item.length) && item.length > 0 && item.offset + item.length <= body.length)
    .sort((left, right) => left.offset - right.offset);
  if (!descriptors.length) return <>{body}</>;

  const content: ReactNode[] = [];
  let cursor = 0;
  descriptors.forEach((item, index) => {
    if (item.offset < cursor) return;
    if (item.offset > cursor) content.push(body.slice(cursor, item.offset));
    content.push(<CustomEmojiMedia key={`${item.customEmojiId}:${item.offset}:${index}`} item={item} />);
    cursor = item.offset + item.length;
  });
  if (cursor < body.length) content.push(body.slice(cursor));
  return <>{content}</>;
}

export function SafeLibraryMedia({ asset }: { asset: ContactMediaAsset }) {
  if (asset.asset_kind === 'EMOJI') return <span className="contact-library-emoji" aria-hidden="true">{asset.emoji_text || '✨'}</span>;
  const source = safeLibraryMediaUrl(asset.id);
  if (asset.renderMode === 'VIDEO' || String(asset.mime_type || '').toLowerCase().startsWith('video/')) {
    return <video className="contact-library-sticker" src={source} aria-label={asset.title} autoPlay muted loop playsInline preload="metadata" />;
  }
  return <img className="contact-library-sticker" src={source} alt={asset.title} loading="lazy" referrerPolicy="no-referrer" />;
}
